import { TextField, Box } from '@mui/material'
import React, { useRef } from 'react'

export interface CodeInputProps {
  value: string
  onChange: (value: string) => void
  error?: boolean
  disabled?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
  onEnter?: () => void
  digits?: number
}

export default function CodeInput({
  value,
  onChange,
  error,
  disabled,
  onEnter,
  inputRef,
  digits = 6
}: CodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const setInputRef = (el: HTMLInputElement | null, idx: number) => {
    inputRefs.current[idx] = el
    if (idx === 0 && inputRef) {
      ;(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const digit = e.target.value.replace(/[^0-9]/g, '')
    if (digit.length > 1) return

    const newValue = value.split('')
    newValue[idx] = digit
    onChange(newValue.join(''))

    if (digit && idx < digits - 1) {
      inputRefs.current[idx + 1]?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Backspace') {
      const newValue = value.split('')
      newValue[idx] = ''
      onChange(newValue.join(''))

      setTimeout(() => {
        if (idx > 0) {
          inputRefs.current[idx - 1]?.focus()
        } else {
          inputRefs.current[0]?.focus()
        }
      }, 0)
    }

    if (e.key === 'Enter' && onEnter) {
      onEnter()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData('Text').replace(/[^0-9]/g, '')
    if (paste.length === digits) {
      onChange(paste)
      inputRefs.current[digits - 1]?.focus()
      e.preventDefault()
    }
  }

  return (
    <Box display="flex" gap={1} justifyContent="center" aria-label="Verification code input">
      {[...Array(digits)].map((_, idx) => (
        <TextField
          className="flex-1"
          key={idx}
          id={`code-input-${idx}`}
          inputRef={el => setInputRef(el, idx)}
          inputProps={{
            maxLength: 1,
            style: { textAlign: 'center', fontSize: 24 },
            'aria-label': `Digit ${idx + 1}`
          }}
          value={value[idx] || ''}
          onChange={e => handleChange(e as React.ChangeEvent<HTMLInputElement>, idx)}
          onKeyDown={e => handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, idx)}
          onPaste={handlePaste}
          error={error}
          disabled={disabled}
          variant="outlined"
        />
      ))}
    </Box>
  )
}
